// utils/stateTransitionHandler.ts

import { ConversationOption, getInitialOptions } from "features";

// The "Do you have a job description already?" choice. The backend sends these options on
// the live response but does NOT persist them in conversation history, so on reload there's
// nothing to restore — leaving the recruiter stuck at get_jd_choice with no buttons and no
// input. The option *values* (upload / generate) are what the backend acts on; the labels are
// display-only. Keep this in sync with the live get_jd_choice options.
const JD_CHOICE_OPTIONS: ConversationOption[] = [
  { text: "Yes, I have one", value: "upload" },
  { text: "No, generate one for me", value: "generate" },
];

interface StateTransitionParams {
  state: string;
  options: ConversationOption[] | null;
  setShowOption: (value: boolean) => void;
  setShowInput: (value: boolean) => void;
  setCurrentOptions: (options: ConversationOption[] | null) => void;
  /** The response carried a `control` — the form owns its own submit button. */
  hasControl?: boolean;
  /** The response requested a composer through a text-area or upload hint. */
  wantsComposer?: boolean;
}

export const handleStateTransition = ({
  state,
  options,
  setShowOption,
  setShowInput,
  setCurrentOptions,
  hasControl,
  wantsComposer,
}: StateTransitionParams) => {
  // A server-driven control is self-contained: it renders its own options and
  // submit button, so neither the composer nor the option pills belong under it.
  if (hasControl) {
    setShowOption(false);
    setShowInput(false);
    setCurrentOptions(null);
    return;
  }

  // The assistant explicitly requested a text/upload composer and no buttons.
  // Deliberately not applied when options exist: letting a composer hint override
  // a response that also carries choices could hide those buttons and dead-end
  // an existing flow.
  if (wantsComposer && !(options && options.length > 0)) {
    setShowInput(true);
    setShowOption(false);
    setCurrentOptions(null);
    return;
  }

  switch (state) {
    case "get_jd_choice":
      // History doesn't persist these options, so fall back to the canonical set.
      // Without this, showOption is true but currentOptions is empty → no buttons render
      // and the recruiter is blocked (and the empty options defeat the stuck-state safety net).
      setCurrentOptions(options && options.length > 0 ? options : JD_CHOICE_OPTIONS);
      setShowOption(true);
      setShowInput(false);
      break;

    case "create_jd":
    case "show_job_criteria":
    case "submit_jd":
      setShowOption(false);
      setShowInput(false);
      break;

    case "upload_jd":
    case "get_jd_upload":
    case "get_role":
      // The assistant is asking the recruiter to provide/upload a JD (or the role) —
      // the text input must be available. Mirrors the live path, which sets showInput
      // true for these states; without this, a refresh here hits `default` and (when
      // options exist) hides the input, dead-ending the flow.
      setShowInput(true);
      setShowOption(false);
      break;

    case "get_experience_level":
      // Self-contained form (experience level / years) — no text input or option list.
      // Mirrors the live path; without this, a refresh here falls to `default` and
      // (with no options) leaves the input lingering under the form.
      setShowOption(false);
      setShowInput(false);
      break;

    case "technical_competence":
      if (options && options.length > 0) {
        setCurrentOptions(options);
        setShowOption(true);
        setShowInput(false);
      } else {
        setShowOption(false);
        setShowInput(false);
      }
      break;

    case "welcome":
      setShowOption(true);
      setShowInput(false);
      setCurrentOptions(getInitialOptions());
      break;

    case "criteria":
      if (options && options.length > 0) {
        setCurrentOptions(options);
        setShowOption(true);
      } else {
        setShowOption(false);
      }
      setShowInput(false);
      break;

    case "set_timeline":
    case "timeline_complete":
      setShowOption(!!options?.length);
      setShowInput(false);
      break;

    case "display_results":
      // CV comparison table has its own embedded option buttons — hide both input and standalone options
      setShowOption(false);
      setShowInput(false);
      break;

    default:
      if (options && options.length > 0) {
        setCurrentOptions(options);
        setShowOption(true);
        setShowInput(false);
      } else {
        setShowOption(false);
        setShowInput(true); // fallback to input on unknown states without options
      }
      break;
  }
};
